import { CdkDragDrop, DragDropModule, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyMenuModule } from '@angular/material/legacy-menu';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Store } from '@ngxs/store';
import { TaskStatusService } from '@tasks/services/task-status.service';
import { TasksService } from '@tasks/services/tasks.service';
import { TaskActions } from '@tasks/state/actions';
import { TaskState } from '@tasks/state/state';
import { TaskStatus, Task } from '@tasks/tasks.types';
import { TaskUtils } from '@tasks/tasks.utils';
import { filter, Observable } from 'rxjs';

@Component({
  selector: 'app-task-view-kanban',
  standalone: true,
  imports: [CommonModule, DragDropModule, RouterModule, MatLegacyMenuModule, MatIconModule],
  template: `
    <div class="tasks-kanban">
      <div class="kanban-container" cdkDropListGroup>
        <div
          [ngClass]="{ 'kanban-group': true, 'no-data': groupedTasksByStatus[workflow.key]?.length === 0 }"
          [id]="workflow.key"
          *ngFor="let workflow of workflowStates; let i = index; trackBy: trackByFn"
          (cdkDropListDropped)="droppedKanban($event)"
          cdkDropList
          [cdkDropListData]="groupedTasksByStatus[workflow.key]"
          cdkDragBoundary=".kanban-container"
        >
          <div class="kanban-header">
            <div class="kanban-item-title">{{ workflow.label }}</div>
          </div>
          <ng-container *ngIf="!(groupedTasksByStatus[workflow.key]?.length === 0)">
            <treo-card
              class="flex items-start justify-between p-3 m-0 mb-4 rounded border-l-4"
              *ngFor="let task of groupedTasksByStatus[workflow.key]; let j = index; trackBy: trackByFn"
              cdkDrag
              [cdkDragData]="task"
            >
              <ng-container *ngIf="task?.id">
                <div class="flex flex-col flex-1" (click)="openTaskDetails(task)">
                  <div class="text-base font-semibold leading-tight mb-1">
                    {{ task?.title || '-' }}
                  </div>
                  <div class="text-md text-primary leading-tight mb-1">
                    {{ getTaskStatusLabel(task?.workflow_state) || '-' }}
                  </div>
                  <div class="text-md text-secondary">
                    {{ task?.assigned_user_name || '-' }}
                  </div>
                </div>
                <button mat-icon-button [matMenuTriggerFor]="itemAction" class="self-start more-actions">
                  <mat-icon class="text-dark" [svgIcon]="'more_vert'"></mat-icon>
                </button>
                <mat-menu #itemAction="matMenu" class="kanban-dropdown-menu">
                  <button mat-menu-item class="py-4" (click)="deleteTask(task)">
                    <mat-icon [svgIcon]="'delete'"></mat-icon>
                    <span>Delete</span>
                  </button>
                </mat-menu>

                <div *cdkDragPreview id="kanban-preview" class="flex items-start justify-between rounded border-l-4">
                  <div class="flex flex-col flex-1">
                    <div class="text-lg font-semibold leading-tight mb-1">
                      {{ task?.title || '-' }}
                    </div>
                    <div class="text-md text-primary leading-tight mb-1">
                      {{ getTaskStatusLabel(task?.workflow_state) || '-' }}
                    </div>
                    <div class="text-md text-secondary">
                      {{ task?.assigned_user_name || '-' }}
                    </div>
                  </div>
                </div>
                <div *cdkDragPlaceholder id="kanban-placeholder"></div>
              </ng-container>
            </treo-card>
          </ng-container>
        </div>
      </div>
    </div>
  `,
  styleUrl: './task-view-kanban.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskViewKanbanComponent implements OnInit {
  private taskStatusService = inject(TaskStatusService);
  private store = inject(Store);
  private tasks$: Observable<Task[]> = this.store.select(TaskState.getTasks).pipe(filter((tasks) => !!tasks));
  private changeDetectorRef = inject(ChangeDetectorRef);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);

  workflowStates: TaskStatus[];
  groupedTasksByStatus: { [key: string]: Task[] } = {};

  ngOnInit(): void {
    this.workflowStates = this.taskStatusService.getAvailableWorkflowStates();
    this.tasks$.subscribe((tasks) => {
      this.groupedTasksByStatus = TaskUtils.groupTasksByStatus(TaskUtils.flattenTasks(tasks));
      this.changeDetectorRef.detectChanges();
    });
  }

  public droppedKanban(event: CdkDragDrop<any[]>): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
      const updatedTask = event.item.data;
      updatedTask.workflow_state = event.container.id;
      this.store.dispatch(new TaskActions.UpdateTask(updatedTask));
    }
    this.changeDetectorRef.markForCheck();
  }

  getTaskStatusLabel(workflow: string): string {
    return TaskUtils.getTaskStatusLabel(workflow);
  }

  deleteTask(task: Task): void {
    this.store.dispatch(new TaskActions.DeleteTask(task.task_list_id, task.id));
  }

  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  openTaskDetails(task: Task) {
    this.store.dispatch(new TaskActions.GetTaskDetail(task.task_list_id, task.id)).subscribe(() => {
      this.router.navigate([{ outlets: { details: ['details', task.id] } }], {
        relativeTo: this.activatedRoute.parent,
      });
    });
  }
}
